import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyApi } from '../utils/api';
import { SessionManager } from '../components/sessions/SessionManager';
import { SessionHistory } from '../components/sessions/SessionHistory';
import { sessionsAPI } from '../api/sessions';
import type { Survey, Question } from '../types';
import { PageLayout, Card, Button, Alert } from '../components/ui';

const SurveySessionPage: React.FC = () => {
  const intl = useIntl();
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'history' | 'taking'>('history');
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (surveyId) {
      loadSurveyData();
    }
  }, [surveyId]);

  const loadSurveyData = async () => {
    if (!surveyId) return;

    try {
      setLoading(true);
      const surveyData = await surveyApi.getSurvey(surveyId);

      setSurvey(surveyData);
      // Questions are already included in the survey data
      setQuestions(surveyData.questions || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || intl.formatMessage({ id: 'session.failedToLoad' }));
    } finally {
      setLoading(false);
    }
  };

  const handleStartNew = async () => {
    if (!surveyId) return;

    try {
      const session = await sessionsAPI.startSession(surveyId);
      setSessionId(session.id);
      setMode('taking');
    } catch (err: any) {
      setError(err.response?.data?.detail || intl.formatMessage({ id: 'session.failedToStart' }));
    }
  };

  const handleResume = (resumeSessionId: string) => {
    setSessionId(resumeSessionId);
    setMode('taking');
  };

  const handleComplete = () => {
    // Refresh the page to show updated history
    setMode('history');
    loadSurveyData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-foreground-muted"><FormattedMessage id="session.loadingSurvey" /></p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="error" className="max-w-md">
          {error || <FormattedMessage id="session.surveyNotFound" />}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="ml-4 underline"
          >
            <FormattedMessage id="session.returnToDashboard" />
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <PageLayout maxWidth="md">
      {mode === 'history' ? (
        <div>
          <Card elevated className="mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">{survey.title}</h1>
            {survey.description && (
              <p className="text-foreground-muted">{survey.description}</p>
            )}
            {survey.min_days_between_attempts != null && survey.min_days_between_attempts > 0 && (
              <div className="mt-4 text-sm text-primary">
                <FormattedMessage id="session.retakesAllowed" values={{ days: survey.min_days_between_attempts }} />
              </div>
            )}
          </Card>

          <SessionHistory
            surveyId={surveyId!}
            onResume={handleResume}
            onStartNew={handleStartNew}
          />
        </div>
      ) : (
        <SessionManager
          survey={survey}
          questions={questions}
          sessionId={sessionId}
          onComplete={handleComplete}
        />
      )}
    </PageLayout>
  );
};

export default SurveySessionPage;